import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { DatePicker, Select } from 'antd';
import {
  Buildings,
  CalendarBlank,
  ListChecks,
  Phone,
  Smiley,
} from '@phosphor-icons/react';
import Typography from '../../../components/Typography';
import COLORS from '../../../util/constants/colors';
import { mairaRangeDisabledDate } from '../MairaFilters';
import { SENTIMENT_BUCKETS } from '../MairaSentiment';
import { UNKNOWN_OUTCOME } from '../MairaOutcomes';

const DATE_FORMAT = 'YYYY-MM-DD';

const sentimentSelectOptions = SENTIMENT_BUCKETS.map(({ bucket, label }) => ({
  value: bucket,
  label: `${label} (${bucket})`,
}));

const outcomeSelectOptions = (taxonomy) => [
  ...(taxonomy?.outcomes || []).map((outcome) => ({
    value: outcome.label,
    label: outcome.display_label || outcome.label,
  })),
  { value: UNKNOWN_OUTCOME, label: 'Unknown' },
];

// The report builder's filter row. Presentational: it renders the controls and
// reports each change up; the parent owns the resulting filter state, the build/
// export actions, and the request lifecycle.
const ReportFilterInputs = ({
  dateRange,
  onDateRangeChange,
  voiceAiNumberIds,
  onVoiceAiNumberIdsChange,
  departmentSlugs,
  onDepartmentSlugsChange,
  sentimentBucket,
  onSentimentBucketChange,
  outcome,
  onOutcomeChange,
  voiceAiNumbers,
  departments,
  taxonomy,
}) => {
  // RangePicker-local: tracks the in-progress selection so disabledDate can clamp
  // the second pick to the max window. Nothing outside the picker needs it.
  const [calendarDates, setCalendarDates] = useState([]);

  return (
    <>
      <div className="maira-report-input-item maira-report-date">
        <Typography variant="text" size="small" bold>
          <CalendarBlank size={16} color={COLORS.BLUE_PRIMARY_LITE} weight="bold" />
          &nbsp;
          Date Range
        </Typography>
        <DatePicker.RangePicker
          allowClear={false}
          value={[
            dateRange.startDate ? moment(dateRange.startDate, DATE_FORMAT) : null,
            dateRange.endDate ? moment(dateRange.endDate, DATE_FORMAT) : null,
          ]}
          format={DATE_FORMAT}
          disabledDate={(current) => mairaRangeDisabledDate(current, calendarDates)}
          onCalendarChange={(nextCalendarDates) => setCalendarDates(nextCalendarDates || [])}
          onChange={(_, dateStrings) => {
            onDateRangeChange({
              startDate: dateStrings[0] || null,
              endDate: dateStrings[1] || null,
            });
            setCalendarDates([]);
          }}
          onOpenChange={(open) => {
            if (!open) setCalendarDates([]);
          }}
        />
      </div>
      <div className="maira-report-input-item">
        <Typography variant="text" size="small" bold>
          <Phone size={16} color={COLORS.BLUE_PRIMARY_LITE} weight="bold" />
          &nbsp;
          Voice AI Numbers
        </Typography>
        <Select
          mode="multiple"
          allowClear
          placeholder="All numbers"
          value={voiceAiNumberIds}
          options={voiceAiNumbers.map((number) => ({
            value: number.id,
            label: number.label || number.number,
          }))}
          onChange={onVoiceAiNumberIdsChange}
        />
      </div>
      {departments.length > 0 && (
        <div className="maira-report-input-item">
          <Typography variant="text" size="small" bold>
            <Buildings size={16} color={COLORS.BLUE_PRIMARY_LITE} weight="bold" />
            &nbsp;
            Departments
          </Typography>
          <Select
            mode="multiple"
            allowClear
            placeholder="All departments"
            value={departmentSlugs}
            options={departments.map((department) => ({
              value: department.id,
              label: department.name,
            }))}
            onChange={onDepartmentSlugsChange}
          />
        </div>
      )}
      <div className="maira-report-input-item">
        <Typography variant="text" size="small" bold>
          <Smiley size={16} color={COLORS.BLUE_PRIMARY_LITE} weight="bold" />
          &nbsp;
          Sentiment
        </Typography>
        <Select
          allowClear
          placeholder="All sentiment"
          value={sentimentBucket}
          options={sentimentSelectOptions}
          onChange={(value) => onSentimentBucketChange(value || null)}
        />
      </div>
      <div className="maira-report-input-item">
        <Typography variant="text" size="small" bold>
          <ListChecks size={16} color={COLORS.BLUE_PRIMARY_LITE} weight="bold" />
          &nbsp;
          Outcome
        </Typography>
        <Select
          allowClear
          placeholder="All outcomes"
          value={outcome}
          options={outcomeSelectOptions(taxonomy)}
          onChange={(value) => onOutcomeChange(value || null)}
        />
      </div>
    </>
  );
};

ReportFilterInputs.propTypes = {
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  voiceAiNumberIds: PropTypes.array.isRequired,
  onVoiceAiNumberIdsChange: PropTypes.func.isRequired,
  departmentSlugs: PropTypes.array.isRequired,
  onDepartmentSlugsChange: PropTypes.func.isRequired,
  sentimentBucket: PropTypes.string,
  onSentimentBucketChange: PropTypes.func.isRequired,
  outcome: PropTypes.string,
  onOutcomeChange: PropTypes.func.isRequired,
  voiceAiNumbers: PropTypes.array,
  departments: PropTypes.array,
  taxonomy: PropTypes.object,
};

ReportFilterInputs.defaultProps = {
  sentimentBucket: null,
  outcome: null,
  voiceAiNumbers: [],
  departments: [],
  taxonomy: {},
};

export default ReportFilterInputs;
